import { Injectable } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { finalize, of, shareReplay, tap } from 'rxjs'
import type {
  ApiCacheEntry,
  ApiCacheGetOrCreateOptions,
  AnyApiCacheInvalidationOptions,
  ApiCacheKey,
  ApiCacheKeyInput,
  ApiCacheKeyInvalidationOptions,
  ApiCacheClearOptions,
  ApiCacheScopeInvalidationOptions,
  EntityKey
} from './types'
import type { Observable } from 'rxjs'
import type { Options } from '@awork/_shared/services/api-client/ApiClient'

const API_CACHE_DEFAULT_TTL_MS = 15 * 60 * 1000

@Injectable({ providedIn: 'root' })
export class ApiCacheService {
  private readonly entries = new Map<ApiCacheKey, ApiCacheEntry>()
  private readonly keysByScope = new Map<string, Set<ApiCacheKey>>()

  /**
   * Gets a cached response, shared in-flight request, or creates a new request for the cache entry.
   * @param {ApiCacheGetOrCreateOptions<TEntity, T, TKey>} options
   * @returns {Observable<T>}
   */
  getOrCreate<TEntity extends object, TKey extends EntityKey<TEntity>, T>(
    options: ApiCacheGetOrCreateOptions<TEntity, T, TKey>
  ): Observable<T> {
    const key = options.cache.key ?? this.buildKey(options.keyInput)
    const existingEntry = this.entries.get(key) as ApiCacheEntry<T> | undefined

    if (!options.cache.forceRefresh && existingEntry?.inFlight$) {
      return existingEntry.inFlight$
    }

    if (!options.cache.forceRefresh && existingEntry?.request !== undefined && existingEntry.expiresAt > Date.now()) {
      return of(existingEntry.request)
    }

    const entry: ApiCacheEntry<T> = {
      key,
      entity: options.cache.entity,
      property: options.cache.property,
      value: options.cache.value,
      expiresAt: 0
    }

    const request$ = options.request().pipe(
      tap(value => {
        entry.request = value
        entry.expiresAt = Date.now() + (options.cache.ttlMs ?? API_CACHE_DEFAULT_TTL_MS)
      }),
      finalize(() => {
        entry.inFlight$ = undefined

        if (entry.request === undefined) {
          this.deleteEntry(key)
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    )

    entry.inFlight$ = request$
    this.setEntry(entry)

    return request$
  }

  /**
   * Invalidates cached entries by exact keys, structured scope, or all entries.
   * @param {ApiCacheClearOptions | ApiCacheKeyInvalidationOptions} options
   */
  invalidate(options: ApiCacheClearOptions | ApiCacheKeyInvalidationOptions): void
  invalidate<TEntity extends object, TKey extends EntityKey<TEntity> = EntityKey<TEntity>>(
    options: ApiCacheScopeInvalidationOptions<TEntity, TKey>
  ): void
  invalidate(options: AnyApiCacheInvalidationOptions): void
  invalidate(options: AnyApiCacheInvalidationOptions): void {
    if (options.all) {
      this.clear()

      return
    }

    options.keys?.forEach(key => this.deleteEntry(key))

    if ('entity' in options) {
      const scopeKey = this.getScopeKey(options)
      const keys = [...(this.keysByScope.get(scopeKey) ?? [])]

      keys.forEach(key => this.deleteEntry(key))
    }
  }

  /**
   * Clears all cached entries and scope indexes.
   */
  clear(): void {
    this.entries.clear()
    this.keysByScope.clear()
  }

  /**
   * Builds a stable cache key from request identity data.
   * @param {ApiCacheKeyInput} input
   * @returns {ApiCacheKey}
   */
  buildKey(input: ApiCacheKeyInput): ApiCacheKey {
    return JSON.stringify({
      method: input.method,
      url: input.url,
      params: this.getCacheParams(input.options?.params),
      observe: input.options?.observe ?? 'body',
      responseType: input.options?.responseType ?? 'json'
    })
  }

  /**
   * Normalizes request params for cache key generation.
   * @param {Options['params']} params
   * @returns {string}
   */
  private getCacheParams(params?: Options['params']): string {
    if (!params) {
      return ''
    }

    const httpParams = params instanceof HttpParams ? params : this.createHttpParams(params)

    return httpParams
      .keys()
      .sort()
      .flatMap(key => {
        const values = httpParams.getAll(key) ?? []

        return values.sort().map(value => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      })
      .join('&')
  }

  /**
   * Creates HttpParams from record-based request params.
   * @param {Exclude<NonNullable<Options['params']>, HttpParams>} params
   * @returns {HttpParams}
   */
  private createHttpParams(params: Exclude<NonNullable<Options['params']>, HttpParams>): HttpParams {
    let httpParams = new HttpParams()

    Object.entries(params).forEach(([key, value]) => {
      const values = Array.isArray(value) ? value : [value]
      values.forEach(entry => {
        httpParams = httpParams.append(key, entry.toString())
      })
    })

    return httpParams
  }

  /**
   * Stores an entry and indexes it by structured scope.
   * @param {ApiCacheEntry} entry
   */
  private setEntry(entry: ApiCacheEntry): void {
    this.deleteEntry(entry.key)
    this.entries.set(entry.key, entry)

    this.getEntryScopeKeys(entry).forEach(scopeKey => {
      const keys = this.keysByScope.get(scopeKey) ?? new Set<ApiCacheKey>()

      keys.add(entry.key)
      this.keysByScope.set(scopeKey, keys)
    })
  }

  /**
   * Deletes an entry and removes it from scope indexes.
   * @param {ApiCacheKey} key
   */
  private deleteEntry(key: ApiCacheKey): void {
    const entry = this.entries.get(key)

    if (!entry) {
      return
    }

    this.entries.delete(key)

    this.getEntryScopeKeys(entry).forEach(scopeKey => {
      const keys = this.keysByScope.get(scopeKey)

      keys?.delete(key)

      if (!keys?.size) {
        this.keysByScope.delete(scopeKey)
      }
    })
  }

  /**
   * Gets all scope keys that should point to a cached entry.
   * @param {ApiCacheEntry} entry
   * @returns {string[]}
   */
  private getEntryScopeKeys(entry: ApiCacheEntry): string[] {
    return [
      this.getScopeKey({ entity: entry.entity }),
      this.getScopeKey({ entity: entry.entity, property: entry.property }),
      this.getScopeKey({ entity: entry.entity, property: entry.property, value: entry.value })
    ]
  }

  /**
   * Builds a stable internal key for cache scope lookup.
   * @param {Pick<ApiCacheEntry, 'entity'> & Partial<Pick<ApiCacheEntry, 'property' | 'value'>>} scope
   * @returns {string}
   */
  private getScopeKey(
    scope: Pick<ApiCacheEntry, 'entity'> & Partial<Pick<ApiCacheEntry, 'property' | 'value'>>
  ): string {
    const scopeParts = [`entity:${scope.entity.name}`]

    if ('property' in scope) {
      scopeParts.push(`property:${scope.property}`)
    }

    if ('value' in scope) {
      scopeParts.push(`value:${String(scope.value)}`)
    }

    return scopeParts.join('|')
  }
}
